import type { DesignBody } from '@repo/api-contracts';
import { type ReactElement, useCallback, useId, useMemo } from 'react';
import { useQuoteDesignViewQuery } from '@/entities/quote';
import { useModalDialog } from '@/shared/lib';
import { Button, DesignViewer } from '@/shared/ui';

export interface QuoteDesignViewDialogProps {
  readonly quoteRequestId: string;
  /** 견적 사본의 디자인 본문(상세의 `design.designBody`) — 뷰 응답에는 본문이 없다(뷰어 설계 V3). */
  readonly designBody: DesignBody;
  readonly onClose: () => void;
}

/**
 * 디자인 보기 창(뷰어 설계 V2) — 네이티브 모달 `<dialog>`. 데스크톱은 화면 가까이 큰 창(최대 72rem·위아래 2rem 여백), 휴대폰(sm 미만)은
 * 화면을 꽉 채운다. 제목 줄은 제 높이, 본문이 남는 높이를 다 쓰고 뷰어가 그 안에서 늘어난다(아주 낮은 창에서는 본문이 스크롤한다).
 * 마운트가 곧 열림이다: 열려 있는 동안만 뷰를 받고(V13 주기 갱신), Escape·닫기로 닫히면 '디자인 보기' 버튼으로 포커스를 돌려준다.
 * 그리는 재료는 뷰 응답(고정 편집영역·고정 색상·폰트·잉크·고정 이미지)과 상세의 본문뿐이다 — 상품의 지금 편집영역·허용 폰트는 보지 않는다(V4).
 */
export const QuoteDesignViewDialog = ({
  quoteRequestId,
  designBody,
  onClose,
}: QuoteDesignViewDialogProps): ReactElement => {
  const titleId = useId();
  const modal = useModalDialog(onClose, true);
  const viewQuery = useQuoteDesignViewQuery(quoteRequestId, { enabled: true });
  const { refetch } = viewQuery;
  const view = viewQuery.data;
  const ready = view?.status === 'READY' ? view : null;
  // 뷰 없이 한 번이라도 실패했으면 실패 안내를 둔다. 데이터 없이 다시 받으면 쿼리는 오류를 지우고 pending 으로 돌아가므로
  // (TanStack Query v5) isError 만 보면 다시 시도하는 동안 안내가 '불러오는 중'으로 바뀐다 — 실패 횟수로 안내를 지킨다.
  const failed = view === undefined && (viewQuery.isError || viewQuery.errorUpdateCount > 0);
  const assetUrls = useMemo(
    () => new Map((ready?.assets ?? []).map((asset): [string, string] => [asset.id, asset.url])),
    [ready],
  );
  // 서명 주소가 만료(403)되면 뷰어가 부른다 — 새 주소를 다 받은 뒤 그 면을 다시 그린다(V12).
  // refetch() 는 쿼리 캐시를 새 뷰로 바꾼 뒤에 끝나므로, 이 함수가 끝나면 곧 이어지는 렌더에서 새 주소가 뷰어에 들어간다.
  const refreshUrls = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  return (
    <dialog
      ref={modal.ref}
      onCancel={modal.onCancel}
      onClose={modal.onClose}
      aria-modal="true"
      aria-labelledby={titleId}
      className="m-0 h-dvh max-h-none w-full max-w-none bg-background p-0 text-foreground backdrop:bg-foreground/40 sm:m-auto sm:h-[calc(100dvh-4rem)] sm:w-[calc(100%-4rem)] sm:max-w-6xl sm:rounded-lg sm:border sm:shadow-lg"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-5">
          <h2 id={titleId} className="font-medium">
            디자인 보기
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-11 min-w-11 sm:min-h-8"
            onClick={onClose}
          >
            닫기
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 sm:p-5">
          {failed ? (
            <div role="alert" className="m-auto flex flex-col items-center gap-3 text-sm">
              <p className="text-destructive">디자인을 불러오지 못했습니다</p>
              {/* 다시 받는 동안에는 잠가 겹쳐 누르지 못하게 한다. */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 sm:min-h-8"
                isLoading={viewQuery.isFetching}
                onClick={() => {
                  void refetch();
                }}
              >
                {viewQuery.isFetching ? '다시 불러오는 중…' : '다시 시도'}
              </Button>
            </div>
          ) : view === undefined ? (
            <p role="status" className="m-auto text-sm text-muted-foreground">
              디자인을 불러오는 중…
            </p>
          ) : ready === null ? (
            <p role="status" className="m-auto text-sm text-muted-foreground">
              이 견적의 디자인은 지금 보기에서 열 수 없습니다
            </p>
          ) : (
            <DesignViewer
              definition={ready.definition}
              colorOptionId={ready.colorOptionId}
              body={designBody}
              assetUrls={assetUrls}
              fonts={ready.fonts}
              inks={ready.inkColors}
              refreshUrls={refreshUrls}
              className="min-h-0 flex-1"
            />
          )}
        </div>
      </div>
    </dialog>
  );
};
